import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Fingerprint, Bell, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { isNative, pushEnabled } from "@/lib/native";
import { biometryAvailable, getBioPref, setBioPref, biometricUnlock, clearTokens } from "@/lib/tokens";
import { registerPush } from "@/lib/push";

const Row = ({ icon: Icon, title, desc, children }) => (
  <div className="flex items-center gap-4 border-b border-stone-200 py-5 last:border-0">
    <Icon className="h-5 w-5 shrink-0 text-stone-500" strokeWidth={1.75} />
    <div className="min-w-0 flex-1"><p className="text-sm font-medium text-stone-900">{title}</p><p className="text-xs text-stone-500">{desc}</p></div>
    {children}
  </div>
);

const BiometricRow = () => {
  const [avail, setAvail] = useState(false);
  const [on, setOn] = useState(false);
  useEffect(() => { biometryAvailable().then(setAvail); getBioPref().then(setOn); }, []);
  const toggle = async (v) => {
    if (v && !(await biometricUnlock())) return;
    await setBioPref(v);
    setOn(v);
    toast.success(v ? "Face ID / fingerprint unlock turned on" : "Biometric unlock turned off");
  };
  return (
    <Row icon={Fingerprint} title="Face ID / fingerprint unlock" desc={avail ? "Ask for biometrics each time you open the app" : "Not available on this device"}>
      <Switch data-testid="biometric-switch" disabled={!avail} checked={on} onCheckedChange={toggle} />
    </Row>
  );
};

const PushRow = () => {
  const nav = useNavigate();
  const enable = async () => {
    const r = await registerPush((p) => nav(p)).catch(() => "unavailable");
    toast[r === "granted" ? "success" : "error"](r === "granted" ? "Notifications on" : r === "denied" ? "Allow notifications in your phone settings" : "Notifications aren't set up yet");
  };
  return (
    <Row icon={Bell} title="Push notifications" desc={pushEnabled ? "Get alerts for messages, offers and requests" : "Coming soon. You'll still get email alerts."}>
      <Button size="sm" variant="outline" data-testid="enable-push-button" disabled={!pushEnabled} onClick={enable}>Enable</Button>
    </Row>
  );
};

const DeleteAccount = () => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: pw });
      await clearTokens();
      setUser(false);
      toast.success("Your account has been deleted");
      nav("/login");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <>
      <Row icon={Trash2} title="Delete my account" desc="Permanently removes your account, profile, messages and uploaded files">
        <Button size="sm" variant="outline" data-testid="delete-account-button" onClick={() => setOpen(true)} className="text-red-600">Delete</Button>
      </Row>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Delete your account?</DialogTitle><DialogDescription>This can't be undone. Enter your password to confirm.</DialogDescription></DialogHeader>
          <Input data-testid="delete-account-password" type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <DialogFooter><Button data-testid="delete-account-confirm" disabled={!pw || busy} onClick={del} className="bg-red-600 hover:bg-red-700">{busy ? "Deleting…" : "Delete permanently"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function Account() {
  const { user } = useAuth();
  return (
    <div data-testid="account-page" className="max-w-2xl">
      <PageHeader eyebrow="Settings" title="Account" desc={`Signed in as ${user?.email}`} />
      <section className="rounded-md border border-stone-200 bg-white px-5">
        {isNative && <BiometricRow />}
        {isNative && <PushRow />}
        <Row icon={ShieldCheck} title="Privacy policy" desc="How we handle your data"><Link to="/privacy" data-testid="privacy-link" className="text-sm font-medium underline">View</Link></Row>
        {user?.role !== "admin" && <DeleteAccount />}
      </section>
    </div>
  );
}
